/**
 * Month-end awards.
 *
 * Two kinds, and the difference is the point of the design:
 *
 * - **Social awards** are zero-sum. One winner, decided by beating everyone
 *   else. They only exist for months that have finished.
 * - **Personal awards** are not. You earn them by clearing a bar, so everybody
 *   can hold the same trophy, and they light up during the month as you go.
 *
 * Both are computed from the workout log rather than stored. That means a
 * backdated lift can still change a past month's winner; see docs/AWARDS.md for
 * why that is acceptable for now and what sealing them would involve.
 */

export type SocialAwardType =
  'champion' | 'champion_female' | 'most_improved' | 'heaviest' | 'heaviest_female' | 'volume';

export type PersonalAwardType = 'participation' | 'finisher' | 'workouts';

export interface SocialAward {
  type: SocialAwardType;
  userId: string;
  displayName: string;
  /** The number that won it. */
  value: number;
  unit: 'points' | 'lb';
  /** The evidence, e.g. "Conventional Deadlift · 405 lb × 1 · Aug 22". */
  detail: string;
}

export interface PersonalAward {
  type: PersonalAwardType;
  earned: boolean;
  /** Present while unearned, for the progress bar. */
  progress?: { have: number; need: number };
  /** The workout date it was earned on, once earned. */
  earnedDate?: string;
}

/** One month's results. */
export interface MonthAwards {
  monthKey: string;
  social: SocialAward[];
}

/** One month of one person's trophy case: what they won and what they earned. */
export interface TrophyMonth {
  monthKey: string;
  isCurrentMonth: boolean;
  social: SocialAward[];
  personal: PersonalAward[];
}

/** Display metadata. Kept beside the types so a new award is one edit. */
export const SOCIAL_AWARD_META: Record<
  SocialAwardType,
  { label: string; short: string; icon: string }
> = {
  champion: { label: 'Champion', short: 'Champion', icon: '🏆' },
  champion_female: { label: 'Champion · Women', short: 'Champion ♀', icon: '🥇' },
  most_improved: { label: 'Most improved', short: 'Most improved', icon: '📈' },
  heaviest: { label: 'Heaviest lift', short: 'Heaviest', icon: '🏋️' },
  heaviest_female: { label: 'Heaviest lift · Women', short: 'Heaviest ♀', icon: '🥈' },
  volume: { label: 'Volume', short: 'Volume', icon: '📊' },
};

export const PERSONAL_AWARD_META: Record<PersonalAwardType, { label: string; icon: string }> = {
  participation: { label: 'Participation', icon: '🎖️' },
  finisher: { label: 'Finisher', icon: '🏅' },
  // Overridden per month by MONTHLY_TROPHY below.
  workouts: { label: '4 Workouts', icon: '🗓️' },
};

/** How many training days the monthly workout trophy asks for. */
export const WORKOUT_TROPHY_TARGET = 4;

/**
 * The monthly workout trophy gets a seasonal name.
 *
 * Keyed by calendar month (1-12). Only the ones we have named are here; every
 * other month falls back to the generic label above, so the cabinet never has a
 * hole in it and naming a new month is a one-line change.
 */
export const MONTHLY_TROPHY: Record<number, { label: string; icon: string }> = {
  8: { label: 'Dog Days', icon: '🐕' },
  9: { label: 'Back to Schwool', icon: '🎒' },
  10: { label: 'Spooky Swol', icon: '👻' },
};

/** The name and icon of the workout trophy for a given month key ("2026-08"). */
export function workoutTrophyMeta(monthKey: string): { label: string; icon: string } {
  const month = Number(monthKey.slice(5, 7));
  return MONTHLY_TROPHY[month] ?? PERSONAL_AWARD_META.workouts;
}
